import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (next: T) => void;
  options: ReadonlyArray<SegmentedOption<T>>;
  label?: string;
  /**
   * When present, a small "?" icon is rendered next to the label with this
   * text as a hover/tap tooltip. Used for filter definitions ("how is this
   * computed?") without stealing a row of chrome.
   */
  labelHint?: React.ReactNode;
  ariaLabel?: string;
  id?: string;
  /**
   * xs — compact variant used when many controls share a single row (e.g.
   * the desktop FilterBar with Granularity + DayFilter visible). Shrinks
   * both height and horizontal padding but keeps the same tap/click target
   * semantics (role=radio). Callers passing 'sm' or 'md' are unaffected.
   */
  size?: 'xs' | 'sm' | 'md';
  /**
   * When present, the axis does not apply on the current tab: the segments go
   * muted and non-interactive and the reason is attached to the group. The
   * control still RENDERS its current value, because that value is real — it
   * is set globally and it bites on other tabs — it just cannot narrow this
   * one. See lib/filter-applicability.ts for why this is disabled rather than
   * unmounted.
   */
  inapplicableReason?: string;
  className?: string;
  'data-testid'?: string;
}

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  label,
  labelHint,
  ariaLabel,
  id,
  size = 'md',
  inapplicableReason,
  className,
  'data-testid': testId,
}: SegmentedControlProps<T>) {
  const inapplicable = Boolean(inapplicableReason);
  const segHeight =
    size === 'xs' ? 'h-7' : size === 'sm' ? 'h-8' : 'h-9';
  const segPad =
    size === 'xs' ? 'px-2' : size === 'sm' ? 'px-2.5' : 'px-3';

  // HTML `<label for>` only associates with form controls (input, select,
  // textarea, button). Our group is a `<div role="radiogroup">`, so we wire
  // the accessible name via `aria-labelledby` pointing at the label span —
  // this keeps the pairing semantic for AT and silences the Chrome DevTools
  // "for attribute doesn't match any form element" warning.
  const labelId = id ? `${id}-label` : undefined;

  // The "?" help badge renders inline right after the label, so it reads as
  // belonging to the axis it documents.
  // The wrapper carries its own test id (`<testId>-field`) because the
  // radiogroup's `data-testid` sits INSIDE it, as a SIBLING of the label
  // span — so a test that wants to pin "this control carries this label"
  // has nothing to scope to when two controls share a label. Only rendered
  // when the caller supplied a test id.
  return (
    <div
      className={cn('flex items-center gap-2', inapplicable && 'opacity-50')}
      // The reason rides on the wrapper so it is reachable by hovering the
      // LABEL as well as the segments — the label is what the user is reading
      // when he wonders why the control is grey. `title` is the native
      // affordance; the bar has no room for inline prose, and the "?" badge
      // beside three of these labels already establishes hover as where an
      // explanation lives on this strip.
      title={inapplicableReason}
      data-inapplicable={inapplicable ? 'true' : undefined}
      data-testid={testId ? `${testId}-field` : undefined}
    >
      {label && (
        <span id={labelId} className="inline-flex items-center text-sm">
          {label}
        </span>
      )}
      {label && labelHint && (
        <SegmentedHelp label={label}>{labelHint}</SegmentedHelp>
      )}
      <div
        id={id}
        role="radiogroup"
        aria-labelledby={label ? labelId : undefined}
        aria-label={label ? undefined : ariaLabel}
        aria-disabled={inapplicable || undefined}
        // Announced to a screen reader, which gets no `title` on a div.
        aria-describedby={inapplicable ? `${labelId ?? testId}-inapplicable` : undefined}
        data-testid={testId}
        className={cn(
          'inline-flex items-center rounded-md border border-input bg-background p-0.5',
          className,
        )}
      >
        {options.map((o) => {
          const active = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={inapplicable}
              onClick={inapplicable ? undefined : () => onChange(o.value)}
              className={cn(
                'inline-flex items-center justify-center rounded-sm text-xs font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                segHeight,
                segPad,
                active
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground',
                !inapplicable &&
                  !active &&
                  'hover:bg-accent hover:text-accent-foreground',
                inapplicable && 'cursor-not-allowed',
              )}
            >
              {o.label}
            </button>
          );
        })}
      </div>
      {inapplicable && (
        <span id={`${labelId ?? testId}-inapplicable`} className="sr-only">
          {inapplicableReason}
        </span>
      )}
    </div>
  );
}

function SegmentedHelp({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`${label} help`}
            className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-input text-[10px] leading-none text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            ?
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs leading-relaxed">
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
